import React from "react";
import { styled } from "@mui/material";
import useSearchValues from "@navigation/SearchModal/useSearchValues";
import { useQuery } from "@tanstack/react-query";
import SquareCollection from "@pageContent/SquareCollection";
import { colors, fonts } from "@constants/cssVariables";
import { useHostConfig } from "@utils/common/getHostConfig";
import { useNoticeError } from "@utils/common/newRelic";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";
import getHeaderAndBodyText from "@utils/search/getHeaderAndBodyText";
import { SearchTabs } from "../SearchModalTabBar/SearchModalTabBar";

const noSearchResultQueryKey = "no-search-result";

const TextContainer = styled("div", { shouldForwardProp: (prop) => prop !== "hasTopMargin" })<{
  hasTopMargin: boolean;
}>(({ theme, hasTopMargin }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(4),
  margin: theme.spacing(16, 0),
  marginTop: hasTopMargin ? theme.spacing(2) : undefined,
  [theme.breakpoints.up("md")]: {
    gap: theme.spacing(6),
    margin: theme.spacing(16, 0, 10),
    marginTop: hasTopMargin ? theme.spacing(4) : undefined,
  },
}));

const Title = styled("p")(({ theme }) => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  maxHeight: 28,
  alignItems: "center",
  WebkitLineClamp: 1,
  color: colors.neutral90,
  font: fonts.header3,
  overflow: "hidden",

  [theme.breakpoints.up("md")]: {
    maxHeight: 40,
  },
}));

const BodyTextWrapper = styled("div")(({ theme }) => ({
  height: 120,

  [theme.breakpoints.up("md")]: {
    height: 78,
  },
}));

const BodyText = styled("p")(() => ({
  font: fonts.bodyMedium,
  color: colors.neutral80,
}));

interface NoSearchResultProps {
  currentTab: SearchTabs;
}

const NoSearchResult: React.FC<NoSearchResultProps> = ({ currentTab }) => {
  const { noSearchResults } = useHostConfig();
  const { searchParam } = useSearchValues();
  const collectionUrl = noSearchResults?.collectionUrl;

  const { data: collection, error } = useQuery<LinkList>({
    queryKey: [noSearchResultQueryKey],
    queryFn: () => fetchByEndpoint<LinkList>(collectionUrl),
    enabled: !!collectionUrl,
  });

  useNoticeError(error, {
    queryKey: noSearchResultQueryKey,
    searchParam,
    collectionUrl,
  });

  if (!noSearchResults) return;

  const { headerText, bodyText, bodyTextSuggestion } = noSearchResults;

  const [noResultHeaderText, noResultBodyText] = getHeaderAndBodyText(currentTab, headerText, bodyText);

  return (
    <React.Fragment>
      <TextContainer hasTopMargin={currentTab === SearchTabs.ALL}>
        <Title>{noResultHeaderText}</Title>
        <BodyTextWrapper>
          <BodyText>
            {noResultBodyText} <b>&lsquo;{searchParam}&rsquo;</b>.
          </BodyText>
          <br />
          <BodyText>{bodyTextSuggestion}</BodyText>
        </BodyTextWrapper>
      </TextContainer>
      {collection && <SquareCollection title={collection.title} items={collection.items} />}
    </React.Fragment>
  );
};

export default NoSearchResult;
